import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const CONSENT_VERSION = "2026-10-research-v1";

/**
 * A brief, optional research choice shown once during onboarding, kept entirely
 * separate from the required setup checklist. Neither choice delays or affects
 * calculating, syncing, logging, or beta access. It appears only for members with
 * no recorded research decision, and "Not now" dismisses it for good (they can
 * still change their mind in Settings).
 */
export const OnboardingResearchCard = () => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/settings/preferences`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (alive && d && d.research_decided === false) setShow(true);
      } catch { /* silent: this card is never blocking */ }
    })();
    return () => { alive = false; };
  }, []);

  const choose = async (body, okMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/settings/preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, research_consent_source: "onboarding" }),
      });
      if (!res.ok) throw new Error();
      setShow(false);
      toast.success(okMsg);
    } catch {
      toast.error("Couldn't save that, try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="rounded-2xl p-5 lg:p-6 pr-card"
      style={{ border: "1px solid var(--pr-card-line)" }}
      data-testid="onboarding-research-card"
    >
      <div className="flex items-start gap-3">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "var(--pr-bg)" }}
        >
          <FlaskConical className="w-4 h-4" style={{ color: "var(--pr-ink)" }} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--pr-muted)" }}>
            Optional · won't affect your beta access
          </p>
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }}>
            Help improve Payday Relay?
          </h2>
          <p className="text-[13px] leading-relaxed mb-4" style={{ color: "var(--pr-ink2)" }}>
            With your permission, we would like to include general patterns from your app activity, such as
            relay progress, time off, and pending-payment outcomes, in aggregate research. Research reports
            won&apos;t include your name, email, or individual dollar amounts. This is optional and won&apos;t
            affect your beta access. You can change your choice anytime in Settings.{" "}
            <Link
              to="/privacy#research"
              className="underline font-semibold"
              style={{ color: "var(--pr-ink)" }}
              data-testid="onboarding-research-privacy-link"
            >
              Read our Privacy Policy
            </Link>
            .
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={() => choose({ research_opt_in: true, research_consent_version: CONSENT_VERSION },
                "Thank you, your activity now counts anonymously.")}
              disabled={busy}
              className="px-5 py-2.5 rounded-full text-sm font-bold pr-btn-primary disabled:opacity-50"
              data-testid="onboarding-research-yes"
            >
              Yes, include my activity
            </button>
            <button
              type="button"
              onClick={() => choose({ research_declined: true }, "No problem, we won't include your activity.")}
              disabled={busy}
              className="px-5 py-2.5 rounded-full text-sm font-bold disabled:opacity-50"
              style={{ border: "1px solid var(--pr-card-line)", color: "var(--pr-ink)", background: "var(--pr-card)" }}
              data-testid="onboarding-research-not-now"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
